import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material';
import { EditNote as EditNoteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../hooks/useOrder';
import PageContainer from '../components/common/PageContainer';
import SectionHeader from '../components/common/SectionHeader';
import OrderSummaryCard from '../components/orders/OrderSummaryCard';
import OrderClientCard from '../components/orders/OrderClientCard';
import OrderItemsTable from '../components/orders/OrderItemsTable';

const ReviewOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { logout, user } = useAuth();
  const {
    order, loading, error, success, setError, setSuccess,
    fetchOrder, updateOrder
  } = useOrder();

  const [confirming, setConfirming] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (id) {
      fetchOrder(id);
    }
  }, [id, fetchOrder]);

  useEffect(() => {
    if (order) {
      setInstructions(order.special_instructions || '');
      setShowInstructions(!!order.special_instructions);
    }
  }, [order]);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError('');
      // Update instructions first
      await updateOrder({ special_instructions: instructions, status: 'confirmado' });
      setSuccess('Pedido confirmado exitosamente');
      setTimeout(() => navigate('/production'), 1500);
    } catch (err) {
      setError('Error al confirmar el pedido');
    } finally {
      setConfirming(false);
    }
  };

  if (!order && !loading) return <PageContainer onLogout={logout}><Alert severity="error">Pedido no encontrado</Alert></PageContainer>;

  return (
    <PageContainer loading={loading} onLogout={() => { logout(); navigate('/login'); }}>
      <SectionHeader
        title={`Revisión del Pedido ${order?.order_number}`}
        subtitle="Verifique los detalles antes de enviar a elaboración."
        breadcrumbs={[
          { label: 'Inicio', path: '/dashboard' },
          { label: 'Pedidos', path: '/orders' },
          { label: `Revisión ${order?.order_number}`, path: '#' }
        ]}
        actions={[
          {
            label: `Estado: ${order?.status_display || 'Borrador'}`,
            variant: 'outlined',
            icon: <EditNoteIcon />,
            color: '#f57c00', borderColor: '#ffb74d'
          }
        ]}
      />

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <OrderItemsTable
            items={order?.items || []}
            onUpdateItem={() => { }} // Could add edit functionality if needed
            onRemoveItem={() => { }}
          />

          <Paper sx={{ p: 2, bgcolor: '#f6f7f8', border: '1px solid #d0dbe7' }}>
            <FormControlLabel
              control={<Checkbox checked={showInstructions} onChange={(e) => setShowInstructions(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Instrucciones Especiales</Typography>}
            />
            {showInstructions && (
              <TextField
                fullWidth multiline rows={2} sx={{ mt: 2, bgcolor: 'white' }}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Ej: Cortar el asado en tiras finas..."
              />
            )}
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <OrderClientCard
            client={order?.client}
            deliveryDate={order?.delivery_date}
            onEdit={user?.is_wholesale_client ? undefined : () => {
              const section = order?.order_type === 'wholesale' ? 'business' : 'public';
              navigate(`/orders/${section}/select-client?orderId=${id}`);
            }}
          />
          <OrderSummaryCard
            order={order}
            confirming={confirming}
            onConfirm={handleConfirm}
            onBack={() => navigate(`/orders/${id}/edit`)}
          />
        </Box>
      </Box>
    </PageContainer>
  );
};

export default ReviewOrder;
